import React, { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const EMPTY = {
  company_name: "", contact_person: "", email: "", phone: "", company_number: "", vat_number: "",
  address: "", postal_codes: [], specialties: [], availability: "", partner_fee_note: "", active: true, documents: "", notes: "",
};

export default function AdminPartners() {
  const [partners, setPartners] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => setPartners([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, postal_codes: p.postal_codes || [], specialties: p.specialties || [] }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        postal_codes: typeof form.postal_codes === "string" ? form.postal_codes.split(",").map((s) => s.trim()).filter(Boolean) : form.postal_codes,
        specialties: typeof form.specialties === "string" ? form.specialties.split(",").map((s) => s.trim()).filter(Boolean) : form.specialties,
      };
      if (editing) await api.put(`/admin/partners/${editing.id}`, payload);
      else await api.post("/admin/partners", payload);
      toast.success("Partner opgeslagen");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const toggleActive = async (p) => { await api.put(`/admin/partners/${p.id}`, { ...p, active: !p.active }); load(); };
  const del = async (p) => { if (window.confirm("Partner verwijderen?")) { await api.delete(`/admin/partners/${p.id}`); load(); } };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Partners</h1>
        <Button className="cta-orange rounded-full" onClick={openNew} data-testid="add-partner-btn"><Plus size={16} className="mr-1" /> Partner toevoegen</Button>
      </div>

      {partners === null ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {partners.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`partner-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading font-bold text-slate-900">{p.company_name}</p>
                  <p className="text-sm text-slate-500">{p.contact_person}</p>
                </div>
                <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={`partner-active-${p.id}`} />
              </div>
              <p className="text-sm text-slate-600 mt-2">{p.email}</p>
              <p className="text-sm text-slate-600">{p.phone}</p>
              <p className="text-xs text-slate-400 mt-2">Postcodes: {(p.postal_codes || []).join(", ") || "—"}</p>
              <p className="text-xs text-slate-400">Specialisaties: {(p.specialties || []).join(", ") || "—"}</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-partner-${p.id}`}><Pencil size={14} /></Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(p)} data-testid={`delete-partner-${p.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="partner-dialog">
          <DialogHeader><DialogTitle>{editing ? "Partner bewerken" : "Nieuwe partner"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Bedrijfsnaam" v={form.company_name} on={(v) => setForm({ ...form, company_name: v })} t="partner-company" />
            <Field label="Contactpersoon" v={form.contact_person} on={(v) => setForm({ ...form, contact_person: v })} t="partner-contact" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="E-mail" v={form.email} on={(v) => setForm({ ...form, email: v })} t="partner-email" />
              <Field label="Telefoon" v={form.phone} on={(v) => setForm({ ...form, phone: v })} t="partner-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ondernemingsnr" v={form.company_number} on={(v) => setForm({ ...form, company_number: v })} t="partner-cnr" />
              <Field label="Btw-nummer" v={form.vat_number} on={(v) => setForm({ ...form, vat_number: v })} t="partner-vat" />
            </div>
            <Field label="Adres / werkgebied" v={form.address} on={(v) => setForm({ ...form, address: v })} t="partner-address" />
            <Field label="Postcodes (komma-gescheiden)" v={Array.isArray(form.postal_codes) ? form.postal_codes.join(", ") : form.postal_codes} on={(v) => setForm({ ...form, postal_codes: v })} t="partner-postals" />
            <Field label="Specialisaties (komma-gescheiden)" v={Array.isArray(form.specialties) ? form.specialties.join(", ") : form.specialties} on={(v) => setForm({ ...form, specialties: v })} t="partner-specialties" />
            <Field label="Beschikbaarheid" v={form.availability} on={(v) => setForm({ ...form, availability: v })} t="partner-availability" />
            <div><Label>Interne notities</Label><Textarea className="mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="partner-notes" /></div>
            <label className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="partner-form-active" /> Actief</label>
            <Button className="cta-orange rounded-full w-full" disabled={busy || !form.company_name || !form.email} onClick={save} data-testid="save-partner-btn">{busy ? <Loader2 className="animate-spin" /> : "Opslaan"}</Button>
            {!editing && <p className="text-xs text-slate-400 text-center">Er wordt automatisch een partner-login aangemaakt (wachtwoord: partner123).</p>}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Field({ label, v, on, t }) {
  return <div><Label>{label}</Label><Input className="mt-1" value={v || ""} onChange={(e) => on(e.target.value)} data-testid={t} /></div>;
}
